<template>
  <div class="card">
    <header v-if="customCardTitle" class="card-header">
      <p class="card-header-title">{{ customCardTitle }}</p>
    </header>
    <div>
      <div v-if="!isLoading && !doesUserHaveTeammates" class="card-content has-text-centered">
        <p class="u-m-b is-size-5">StandupPro is better with company!</p>
        <b-button v-if="isLoggedInUserWorkspaceOwner" type="is-info" @click="handleClickInviteUser">
          Invite My Team
        </b-button>
        <p v-else>
          Contact your workspace owner to invite your team members.
        </p>
      </div>

      <div v-if="doesUserHaveTeammates">
        <div v-if="!isArrayEmpty(selectedTeammates) && !isLoading" class="card-content">
          <div v-for="user in selectedTeammates" :key="user.key" class="user-daily-goal__container">
            <UserAvatar
              :user="user"
              :active-goal="getUserGoalMatchingActiveDate(user, activeDateGoals)"
              :isHitRateVisible="isHitRateVisibleOnAvatars"
            />
            <div class="user-name-and-goal__container">
              <span>
                <user-display-name :activeUserId="user.id" size="medium" is-inline />
                <b-tag v-if="isUserOOO(user, activeDateISO)" type="is-warning" size="is-small" class="u-m-l-xsmall">OOO</b-tag>
                <team-tag
                  v-for="team in getUserTeams(user.id, teams)"
                  :key="team.id"
                  :teamId="team.id"
                />
              </span>
              <p
                :class="[
                  'subtitle',
                  'is-size-6',
                  { 'has-text-grey-light': !getUserGoalMatchingActiveDate(user, activeDateGoals) },
                ]"
              >
                {{
                  isUserOOO(user, activeDateISO) && !getUserGoalMatchingActiveDate(user, activeDateGoals)
                    ? 'Out of office'
                    : getUserGoalMatchingActiveDate(user, activeDateGoals)
                      ? getUserGoalMatchingActiveDate(user, activeDateGoals).goalText
                      : 'No goal recorded for this date'
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isLoading" style="position: relative;" class="u-p-b-xlarge">
        <b-loading v-if="isLoading" :is-full-page="false" :active.sync="isLoading"></b-loading>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import isEqual from 'lodash.isequal';
import { defaultDateOptions, isSameDate, dateToISO, isUserOOO } from '@/helpers/dateHelpers';
import { isObjectEmpty, isArrayEmpty } from '@/helpers/dataHelpers';
import { daysOfTheWeek } from '@/constants/TimeAndDateConstants';
import { getUserTeams } from '@/helpers/teamHelpers';
import * as UserActions from '@/store/actions/User.actions';
import * as GoalActions from '@/store/actions/Goal.actions';
import * as TeamActions from '@/store/actions/Team.actions';
import UserAvatar from '@/components/UserAvatar.vue';
import UserDisplayName from '@/components/UserDisplayName.vue';
import TeamTag from '@/components/TeamTag.vue';

export default {
  name: 'TeamGoals',

  components: {
    UserAvatar,
    UserDisplayName,
    TeamTag,
  },

  props: {
    activeTeamIds: {
      type: Array,
      required: true,
    },
    excludedUserIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    activeDate: {
      type: Date,
      required: true,
    },
    customCardTitle: {
      type: String,
      required: false,
    },
    isHitRateVisibleOnAvatars: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      dateOptions: defaultDateOptions,
      areTeammatesLoading: true,
      areTeamGoalsLoading: true,
      areActiveTeamsLoading: true,
    };
  },

  computed: {
    ...mapState({
      users: state => state.user.users,
      teams: state => state.team.teams,
      goals: state => state.goal.goals,
      isUserLoading: state => state.user.isUserLoading,
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
    }),
    ...mapGetters([
      'activeWorkspace',
      'isLoggedInUserWorkspaceOwner',
    ]),
    activeTeams() {
      if (isArrayEmpty(this.activeTeamIds)) return [];

      return this.activeTeamIds.map(teamId => this.teams[teamId]);
    },
    selectedTeammateIds() {
      if (isArrayEmpty(this.activeTeams)) return [];

      return [...new Set([...this.activeTeams
        .map(team => [...team.userIds])
        .flat()
        .filter(id => !this.excludedUserIds.includes(id))])];
    },
    selectedTeammates() {
      return this.selectedTeammateIds.map(id => this.users[id]);
    },
    doesUserHaveTeammates() {
      return !this.isArrayEmpty(this.selectedTeammates);
    },
    activeDateGoals() {
      if (isObjectEmpty(this.goals)) return [];

      return Object.keys(this.goals)
        .map(key => this.goals[key])
        .filter(goal => isSameDate(goal.goalDate, dateToISO(this.activeDate)));
    },
    activeDateISO() {
      return dateToISO(this.activeDate);
    },
    isLoading() {
      return this.areActiveTeamsLoading || this.areTeamGoalsLoading || this.areTeammatesLoading;
    },
    doesActiveDateHaveStandup() {
      if (isArrayEmpty(this.activeTeams) && !this.isLoading) return false;

      const activeDateWeekDayIndex = this.activeDate.getDay();
      const meetingDays = [...new Set(
        this.activeTeams
          .map(team => team.standupMeetingDayIds)
          .flat()
          .map(dayId => daysOfTheWeek.find(day => day.id === dayId)),
      )];

      return meetingDays.find(day => day.weekDayIndex === activeDateWeekDayIndex);
    },
  },

  created() {
    this.fetchInitialData();
  },

  watch: {
    activeTeamIds(newValue, oldValue) {
      if (!isEqual(newValue, oldValue)) {
        this.fetchInitialData();
      }
    },
    activeDate(newValue, oldValue) {
      if (!isSameDate(dateToISO(newValue), dateToISO(oldValue))) {
        this.handleActiveDateChange(newValue);
      }
    },
  },

  methods: {
    isObjectEmpty,
    isArrayEmpty,
    getUserTeams,
    isUserOOO,
    ...mapActions({
      fetchTeamsByIds: TeamActions.FETCH_TEAMS_BY_IDS,
      fetchGoalsByUserIdsByDates: GoalActions.FETCH_GOALS_BY_USER_IDS_BY_DATES,
    }),
    async handleActiveDateChange(newDate) {
      this.areTeamGoalsLoading = true;
      this.activeDate = newDate;
      await this.fetchGoalsByUserIdsByDates({
        userIds: this.selectedTeammateIds,
        dates: [dateToISO(this.activeDate)],
        workspaceId: this.activeWorkspace.id,
      });
      this.areTeamGoalsLoading = false;
    },
    handleClickInviteUser() {
      this.$router.push('/invite-user');
    },
    getUserGoalMatchingActiveDate(user, goals) {
      const matchingGoals = goals.filter(goal => goal.userId === user.id);

      if (matchingGoals.length === 0) return null;

      return matchingGoals[0];
    },
    async fetchInitialData() {
      this.areTeamGoalsLoading = true;
      this.areTeammatesLoading = true;
      this.areActiveTeamsLoading = true;

      await this.fetchTeamsByIds({ teamIds: [...this.activeTeamIds] });
      this.areActiveTeamsLoading = false;

      await this.$store.dispatch(
        UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
        { userIds: this.selectedTeammateIds, workspaceId: this.activeWorkspace.id },
      );
      this.areTeammatesLoading = false;

      await this.fetchGoalsByUserIdsByDates({
        userIds: this.selectedTeammateIds,
        dates: [dateToISO(this.activeDate)],
        workspaceId: this.activeWorkspace.id,
      });
      this.areTeamGoalsLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.user-daily-goal__container {
  display: flex;

  & + .user-daily-goal__container {
    margin-top: $global-whitespace-medium;
  }
}

.user-name-and-goal__container {
  margin-left: $global-whitespace-small;
}
</style>
